import { useEffect, useRef } from 'react';

/**
 * Calls a callback on window scroll events, throttled via `requestAnimationFrame` to avoid layout thrashing.
 * Can be disabled by passing `false` as the second argument.
 */
export function useWindowScroll(callback: VoidFunction, isEnabled = true) {
    const requestAnimationFrameId = useRef<number | null>(null);
    const callbackRef = useRef(callback);

    useEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    useEffect(() => {
        if (!isEnabled) {
            return undefined;
        }

        const handleScroll = () => {
            // Throttle using requestAnimationFrame to avoid layout thrashing
            if (requestAnimationFrameId.current === null) {
                requestAnimationFrameId.current = requestAnimationFrame(() => {
                    callbackRef.current();
                    requestAnimationFrameId.current = null;
                });
            }
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (requestAnimationFrameId.current !== null) {
                cancelAnimationFrame(requestAnimationFrameId.current);
                requestAnimationFrameId.current = null;
            }
        };
    }, [isEnabled]);
}
